import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Slider,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

interface NotificationPreferences {
  enableWebPush: boolean;
  enableMobile: boolean;
  callbackReminders: boolean;
  leadUpdates: boolean;
  taskAssignments: boolean;
  systemUpdates: boolean;
  callbackReminderTime: number;
}

export const NotificationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    enableWebPush: true,
    enableMobile: true,
    callbackReminders: true,
    leadUpdates: true,
    taskAssignments: true,
    systemUpdates: true,
    callbackReminderTime: 30,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get('/api/notification-preferences');
      setPreferences(response.data);
    } catch (err) {
      setError('Failed to load notification preferences');
      toast.error('Could not load your notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put('/api/notification-preferences', preferences);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error('Failed to save notification preferences');
    }
  };

  const handleToggle = (field: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (loading) {
    return <Box>Loading preferences...</Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          Notification Channels
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={preferences.enableWebPush}
              onChange={() => handleToggle('enableWebPush')}
            />
          }
          label="Web Push Notifications"
        />
        <FormControlLabel
          control={
            <Switch
              checked={preferences.enableMobile}
              onChange={() => handleToggle('enableMobile')}
            />
          }
          label="Mobile Notifications"
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>
          Notification Types
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={preferences.callbackReminders}
              onChange={() => handleToggle('callbackReminders')}
            />
          }
          label="Callback Reminders"
        />
        <FormControlLabel
          control={
            <Switch
              checked={preferences.leadUpdates}
              onChange={() => handleToggle('leadUpdates')}
            />
          }
          label="Lead Updates"
        />
        <FormControlLabel
          control={
            <Switch
              checked={preferences.taskAssignments}
              onChange={() => handleToggle('taskAssignments')}
            />
          }
          label="Task Assignments"
        />
        <FormControlLabel
          control={
            <Switch
              checked={preferences.systemUpdates}
              onChange={() => handleToggle('systemUpdates')}
            />
          }
          label="System Updates"
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>
          Timing Preferences
        </Typography>
        <Typography gutterBottom>
          Callback Reminder Time (minutes before)
        </Typography>
        <Slider
          value={preferences.callbackReminderTime}
          onChange={(_, value) => 
            setPreferences(prev => ({ ...prev, callbackReminderTime: value as number }))
          }
          min={5}
          max={60}
          step={5}
          marks
          valueLabelDisplay="auto"
        />
      </Box>

      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
        >
          Save Preferences
        </Button>
      </Box>
    </Box>
  );
};
